import { Customer, User } from "./types";

declare global {
  interface Window {
    aptrinsic: Function;
  }
}

export class GainsightService {
  private aptrinsic: any;

  constructor() {
    this.aptrinsic = window.aptrinsic;
  }

  public isGainsightAvailable(): boolean {
    return !!this.aptrinsic;
  }

  public async waitForGainsight(): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.isGainsightAvailable()) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 10;

      const intervalId = setInterval(() => {
        attempts++;
        this.aptrinsic = window.aptrinsic;

        if (this.isGainsightAvailable()) {
          clearInterval(intervalId);
          resolve();
          return;
        }

        if (attempts >= maxAttempts) {
          clearInterval(intervalId);
          console.warn(`Gainsight not available after ${maxAttempts} attempts`);
          resolve(); // Still resolve to prevent blocking, but with a warning
        }
      }, 500);
    });
  }

  public async initialize(user: User, customer: Customer): Promise<void> {
    await this.waitForGainsight();

    if (!this.isGainsightAvailable()) {
      return;
    }

    const hash = await this.hmacHash(
      `${customer.velocityEhsCustomerNumber}:${customer.id}`,
      user.email
    );

    this.aptrinsic(
      "identify",
      {
        id: hash,
        accelerateAdmin: user.userSegments?.accelerateAdmin || false,
      },
      {
        id: customer.velocityEhsCustomerNumber,
        name: customer.name,
      }
    );
  }

  private async hmacHash(key: string, message: string): Promise<string> {
    const enc = new TextEncoder();
    const cryptoKey = await crypto.subtle.importKey(
      "raw",
      enc.encode(key),
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"]
    );
    const signature = await crypto.subtle.sign(
      "HMAC",
      cryptoKey,
      enc.encode(message)
    );

    return Array.from(new Uint8Array(signature))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
}
